import WolfButton from '@components/ui/WolfButton';
import WolfInput from '@components/ui/WolfInput';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setUserDetails } from '@redux/reducers/user.reducer';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({ isOpen, onComplete }) => {
  const localeUsers = useTranslation('users');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      dispatch(
        appendActionMessage({
          message: 'First name and last name are required',
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    try {
      setLoading(true);

      // Call the backend to complete the profile
      await graphQlClient.request(UserGraphQL.mutations.completeUserProfile, {
        profile: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        },
      });

      // Update the local Redux state with just the firstName and lastName
      dispatch(
        setUserDetails({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      );

      dispatch(
        appendActionMessage({
          message: 'Profile completed successfully!',
          type: SnackType.SUCCESS,
        }),
      );

      // Allow user to continue
      onComplete();
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || 'Failed to complete profile',
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => {}} // Prevent closing
    >
      <DialogTitle>
        <Typography variant="h3" textAlign="center">
          Complete Your Profile
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body14" textAlign="center" sx={{ mb: 3 }}>
            Please provide your first and last name to continue using the application.
          </Typography>

          <WolfInput
            label={localeUsers['firstName']}
            placeholder="Enter your first name"
            value={firstName}
            onChange={handleFirstNameChange}
            fullWidth
            required
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <WolfInput
            label={localeUsers['lastName']}
            placeholder="Enter your last name"
            value={lastName}
            onChange={handleLastNameChange}
            fullWidth
            required
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <WolfButton
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading || !firstName.trim() || !lastName.trim()}
              sx={{ minWidth: 120 }}>
              {loading ? 'Saving...' : 'Complete Profile'}
            </WolfButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
